import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntApp } from 'antd';
import { ThemeProvider, useTheme } from './hooks/useTheme';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ChatManagerProvider } from './hooks/useChatManager';
import { getTheme } from './styles/simpleTheme';
import MainLayout from './components/layout/MainLayout';
import Login from './pages/Login';
import NewChat from './pages/NewChat';
import Chats from './pages/Chats';
import Projects from './pages/Projects';
import CreateProject from './pages/CreateProject';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import './styles/global.css';

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Public Route wrapper (redirects to home if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Navigate to="/" replace /> : <>{children}</>;
};

// App Content with Theme
const AppContent: React.FC = () => {
  const { mode } = useTheme();

  return (
    <ConfigProvider theme={getTheme(mode)}>
      <AntApp>
        <Router>
          <div className="App">
            <Routes>
              {/* Public Route - Redirect to home if already authenticated */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />

              {/* Protected Routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<NewChat />} />
                <Route path="new-chat" element={<NewChat />} />
                <Route path="new-chat/:sessionId" element={<NewChat />} />
                <Route path="chats" element={<Chats />} />
                <Route path="chats/:chatId" element={<Chats />} />
                <Route path="projects" element={<Projects />} />
                <Route path="projects/create" element={<CreateProject />} />
                <Route path="reports" element={<Reports />} />
                <Route path="settings" element={<Settings />} />
              </Route>
            </Routes>
          </div>
        </Router>
      </AntApp>
    </ConfigProvider>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ChatManagerProvider>
          <AppContent />
        </ChatManagerProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
